import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, PackageCheck, Plus, Send, ShoppingCart, Trash2, X } from "lucide-react";

import { inventoryService, type InventoryItem, type PurchaseOrder, type Supplier } from "../../services/inventoryService";
import { useAuthStore } from "../../stores/authStore";
import { apiErrorMessage } from "../../utils/apiError";
import { daysFromTodayIso, todayIso } from "../../utils/dates";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { ActionPanel, DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { ReasonPanel, errorLine } from "./InventoryParts";
import { day, isOpenOrder, lineTotal, money, orderConfig, orderStatus, orderTotal, type OrderLine } from "./inventoryLogic";

const blankLine = (): OrderLine => ({ item: "", quantity: "1", unit_cost: "" });

/** A draft order: who from, what, how many and at what price. Totals follow the lines. */
export function OrderForm({ suppliers, items, initialLines, onClose, onDone }: {
  suppliers: Supplier[];
  items: InventoryItem[];
  initialLines?: OrderLine[];
  onClose: () => void;
  onDone: (order: PurchaseOrder) => void;
}) {
  const today = todayIso();
  const [lines, setLines] = useState<OrderLine[]>(initialLines?.length ? initialLines : [blankLine()]);
  const [tax, setTax] = useState("0");
  const stocked = items.filter((row) => row.is_active).sort((a, b) => a.name.localeCompare(b.name));
  const save = useMutation({ mutationFn: inventoryService.createPurchaseOrder, onSuccess: onDone });
  const update = (index: number, changes: Partial<OrderLine>) => setLines((current) => current.map((line, at) => (at === index ? { ...line, ...changes } : line)));
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    save.mutate({
      order_number: String(data.get("order_number") ?? "").trim() || undefined,
      supplier: Number(data.get("supplier")),
      order_date: String(data.get("order_date")),
      expected_delivery_date: String(data.get("expected") ?? "") || undefined,
      tax_amount: tax || "0",
      notes: String(data.get("notes") ?? "").trim(),
      items: lines.map((line) => ({ item: Number(line.item), quantity_ordered: Number(line.quantity), unit_cost: line.unit_cost })),
    });
  };
  const chosen = new Set(lines.map((line) => line.item).filter(Boolean));

  return (
    <FormModal
      id="purchase-order"
      title="New purchase order"
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The order could not be saved.") : undefined}
      saveLabel="Save as draft"
      note={`Total ${money(orderTotal(lines, tax))}. It is submitted for approval from the order.`}
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The order" divided={false}>
        <Field label="Supplier">
          <select required name="supplier" className="input mt-1">
            <option value="">Choose a supplier</option>
            {suppliers.filter((row) => row.is_active).map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>
        </Field>
        <Field label="Order number" optional hint="Left empty, one is made up, e.g. PO-20260928-A1B2C3.">
          <input name="order_number" maxLength={50} className="input mt-1" />
        </Field>
        <Field label="Order date">
          <input required type="date" name="order_date" defaultValue={today} className="input mt-1" />
        </Field>
        <Field label="Delivery expected" optional>
          <input type="date" name="expected" min={today} defaultValue={daysFromTodayIso(7)} className="input mt-1" />
        </Field>
      </FormSection>

      <FormSection title="What to order" columns={1}>
        <div className="sheet-wrap">
          <table className="sheet">
            <thead>
              <tr>
                <th>Item</th>
                <th className="text-right">Quantity</th>
                <th className="text-right">Unit cost (UGX)</th>
                <th className="text-right">Line total</th>
                <th aria-label="Remove" />
              </tr>
            </thead>
            <tbody>
              {lines.map((line, index) => {
                const item = stocked.find((row) => String(row.id) === line.item);
                return (
                  <tr key={index}>
                    <td className="min-w-[14rem]">
                      <select
                        required
                        aria-label={`Item on line ${index + 1}`}
                        value={line.item}
                        onChange={(event) => {
                          const picked = stocked.find((row) => String(row.id) === event.target.value);
                          update(index, { item: event.target.value, unit_cost: picked?.unit_cost ?? line.unit_cost });
                        }}
                        className="input-compact w-full"
                      >
                        <option value="">Choose an item</option>
                        {stocked.map((row) => (
                          <option key={row.id} value={row.id} disabled={chosen.has(String(row.id)) && String(row.id) !== line.item}>
                            {row.name} ({row.sku}) · {row.current_stock} in stock
                          </option>
                        ))}
                      </select>
                      {item && <span className="mt-0.5 block text-xs text-gray-500">{item.unit_of_measure}</span>}
                    </td>
                    <td className="text-right">
                      <input required aria-label={`Quantity on line ${index + 1}`} type="number" min={1} step={1} value={line.quantity} onChange={(event) => update(index, { quantity: event.target.value })} className="input-compact w-24 text-right tabular-nums" />
                    </td>
                    <td className="text-right">
                      <input required aria-label={`Unit cost on line ${index + 1}`} type="number" min={0} step="any" value={line.unit_cost} onChange={(event) => update(index, { unit_cost: event.target.value })} className="input-compact w-32 text-right tabular-nums" />
                    </td>
                    <td className="text-right tabular-nums">{money(lineTotal(line)) || "—"}</td>
                    <td className="text-right">
                      {lines.length > 1 && (
                        <button type="button" onClick={() => setLines((current) => current.filter((_, at) => at !== index))} className="rounded p-1 text-gray-500 hover:bg-gray-100" aria-label={`Remove line ${index + 1}`}>
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <button type="button" onClick={() => setLines((current) => [...current, blankLine()])} className="btn btn-outline gap-1.5">
            <Plus className="h-4 w-4" /> Add a line
          </button>
          <Field label="Tax (UGX)" optional className="w-40">
            <input type="number" min={0} step="any" value={tax} onChange={(event) => setTax(event.target.value)} className="input mt-1 tabular-nums" />
          </Field>
        </div>
        <Field label="Notes for the supplier" optional>
          <textarea name="notes" rows={2} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Buying stock: draft, approval by someone other than the requester, sent, received line by line. */
export function OrdersPanel({ orders, suppliers, items, isLoading, isError, onRetry, canApprove, onChanged, draft, onDraftDone }: {
  orders?: PurchaseOrder[];
  suppliers: Supplier[];
  items: InventoryItem[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canApprove: boolean;
  onChanged: () => void;
  /** Lines to start a new order with, e.g. everything that needs reordering. */
  draft?: OrderLine[] | null;
  onDraftDone: () => void;
}) {
  const today = todayIso();
  const me = useAuthStore((state) => state.user);
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [receipt, setReceipt] = useState<Record<number, string>>({});
  const done = (text: string, close = false) => {
    setNotice({ tone: "success", text });
    if (close) setViewing(null);
    onChanged();
  };
  const submitOrder = useMutation({ mutationFn: inventoryService.submitPurchaseOrder, onSuccess: (row) => done(`${row.order_number} is waiting for approval.`) });
  const approve = useMutation({ mutationFn: inventoryService.approvePurchaseOrder, onSuccess: (row) => done(`${row.order_number} is approved. Send it to ${row.supplier_name}.`) });
  const send = useMutation({ mutationFn: inventoryService.markPurchaseOrderOrdered, onSuccess: (row) => done(`${row.order_number} is with ${row.supplier_name}. Receive the goods here when they arrive.`) });
  const receive = useMutation({
    mutationFn: ({ id, lines }: { id: number; lines: { id: number; quantity: number }[] }) => inventoryService.receivePurchaseOrder(id, lines),
    onSuccess: (row) => {
      setReceipt({});
      done(row.status === "received" ? `Everything on ${row.order_number} is in; stock is updated.` : `Part of ${row.order_number} received; the rest is still outstanding.`);
    },
  });
  const cancel = useMutation({ mutationFn: ({ id, reason }: { id: number; reason: string }) => inventoryService.cancelPurchaseOrder(id, reason), onSuccess: (row) => done(`${row.order_number} was cancelled.`, true) });
  const remove = useMutation({ mutationFn: inventoryService.deletePurchaseOrder, onSuccess: () => done("The draft was deleted.", true) });

  const columns: RegisterColumn<PurchaseOrder>[] = [
    { header: "Order", cell: (row) => <span className="font-medium text-gray-900">{row.order_number}</span>, exportValue: (row) => row.order_number, className: "whitespace-nowrap" },
    { header: "Supplier", cell: (row) => row.supplier_name, exportValue: (row) => row.supplier_name, className: "text-gray-700" },
    { header: "Ordered", cell: (row) => day(row.order_date), exportValue: (row) => day(row.order_date), className: "tabular-nums text-gray-700" },
    { header: "Expected", cell: (row) => day(row.expected_delivery_date) || <span className="text-gray-300">—</span>, exportValue: (row) => day(row.expected_delivery_date), className: "tabular-nums text-gray-700" },
    { header: "Items", cell: (row) => row.items.map((line) => line.item_name).join(", "), exportValue: (row) => row.items.map((line) => `${line.item_name} x${line.quantity_ordered}`).join("; "), className: "min-w-[12rem] max-w-xs whitespace-normal text-gray-700" },
    { header: "Total", cell: (row) => money(row.total_amount), exportValue: (row) => Number(row.total_amount), align: "right" },
    { header: "Asked for by", cell: (row) => row.requested_by_name, exportValue: (row) => row.requested_by_name, exportOnly: true },
    { header: "Status", cell: (row) => <StatusDot {...orderStatus(row, today)} />, exportValue: (row) => orderStatus(row, today).label },
  ];

  const order = viewing !== null ? orders?.find((row) => row.id === viewing) : undefined;
  const outstanding = order ? order.items.filter((line) => line.outstanding_quantity > 0) : [];
  const ownOrder = order?.requested_by === me?.id;
  const busy = submitOrder.isPending || approve.isPending || send.isPending;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="Purchase orders"
        description="Open an order to move it on: submit, approve, send, receive."
        actions={
          <button type="button" disabled={!suppliers.some((row) => row.is_active) || !items.some((row) => row.is_active)} onClick={() => setCreating(true)} className="btn-primary h-8 min-h-8 px-3 text-xs disabled:opacity-50">
            <ShoppingCart className="mr-1.5 h-3.5 w-3.5" /> New order
          </button>
        }
        rows={orders}
        config={orderConfig}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          [submitOrder, approve, send, receive, cancel, remove].forEach((mutation) => mutation.reset());
          setReceipt({});
          setViewing(row.id);
        }}
        openLabel={(row) => (row.status === "ordered" ? "Receive" : row.status === "pending" && canApprove && row.requested_by !== me?.id ? "Approve" : "Open")}
        noun={["order", "orders"]}
        exportName={{ file: "purchase-orders", title: "Purchase Orders" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No purchase orders yet."
      />

      {order && (
        <DetailsModal
          size="3xl"
          title={`${order.order_number} · ${order.supplier_name}`}
          headline={{ label: "Total", value: money(order.total_amount), note: `${money(order.subtotal)} + ${money(order.tax_amount)} tax` }}
          status={orderStatus(order, today)}
          facts={[
            ["Supplier", order.supplier_name],
            ["Ordered on", day(order.order_date)],
            ["Delivery expected", day(order.expected_delivery_date) || undefined],
            ["Delivered", day(order.actual_delivery_date) || undefined],
            ["Asked for by", order.requested_by_name],
            ["Approved by", order.approved_by_name ? `${order.approved_by_name}${order.approved_date ? `, ${day(order.approved_date)}` : ""}` : undefined],
          ]}
          sections={[{ title: "Notes", body: order.notes }]}
          onClose={() => setViewing(null)}
          footer={
            <>
              {order.status === "draft" && (
                <button type="button" disabled={remove.isPending} onClick={() => remove.mutate(order.id)} className="mr-auto inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50">
                  <Trash2 className="h-4 w-4" /> Delete draft
                </button>
              )}
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {order.status === "draft" && (
                <button type="button" disabled={busy} onClick={() => submitOrder.mutate(order.id)} className="btn btn-primary gap-1.5">
                  <Send className="h-4 w-4" /> Submit for approval
                </button>
              )}
              {order.status === "pending" && canApprove && !ownOrder && (
                <button type="button" disabled={busy} onClick={() => approve.mutate(order.id)} className="btn btn-primary gap-1.5">
                  <CheckCircle2 className="h-4 w-4" /> Approve
                </button>
              )}
              {order.status === "approved" && (
                <button type="button" disabled={busy} onClick={() => send.mutate(order.id)} className="btn btn-primary gap-1.5">
                  <Send className="h-4 w-4" /> Sent to the supplier
                </button>
              )}
            </>
          }
        >
          {order.status === "pending" && (ownOrder || !canApprove) && (
            <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">
              {canApprove ? "You asked for this order, so someone else in school leadership approves it." : "Waiting for school leadership to approve it."}
            </p>
          )}
          {[submitOrder, approve, send, remove].map((mutation, index) => mutation.isError && <div key={index}>{errorLine(mutation.error, "That could not be done.")}</div>)}

          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Lines</h4>
            <div className="sheet-wrap mt-1.5">
              <table className="sheet">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="text-right">Ordered</th>
                    <th className="text-right">Received</th>
                    <th className="text-right">Still to come</th>
                    <th className="text-right">Unit cost</th>
                    <th className="text-right">Line total</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((line) => (
                    <tr key={line.id}>
                      <td className="min-w-[12rem]">{line.item_name} <span className="text-xs text-gray-500">({line.item_sku})</span></td>
                      <td className="text-right tabular-nums">{line.quantity_ordered}</td>
                      <td className="text-right tabular-nums">{line.quantity_received}</td>
                      <td className={`text-right tabular-nums ${line.outstanding_quantity ? "font-medium text-amber-800" : "text-gray-400"}`}>{line.outstanding_quantity}</td>
                      <td className="text-right tabular-nums">{money(line.unit_cost)}</td>
                      <td className="text-right tabular-nums">{money(line.total_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {order.status === "ordered" && outstanding.length > 0 && (
            <ActionPanel title="Receive goods" description="Count what arrived. Stock goes up by what you enter; the rest stays outstanding.">
              <div className="grid gap-3 sm:grid-cols-2">
                {outstanding.map((line) => (
                  <Field key={line.id} label={line.item_name} hint={`${line.outstanding_quantity} still to come`}>
                    <input
                      type="number"
                      min={0}
                      max={line.outstanding_quantity}
                      step={1}
                      value={receipt[line.id] ?? ""}
                      onChange={(event) => setReceipt((current) => ({ ...current, [line.id]: event.target.value }))}
                      className="input mt-1 tabular-nums"
                      placeholder="0"
                    />
                  </Field>
                ))}
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setReceipt(Object.fromEntries(outstanding.map((line) => [line.id, String(line.outstanding_quantity)])))}
                  className="btn btn-outline"
                >
                  Everything arrived
                </button>
                <button
                  type="button"
                  disabled={receive.isPending || !outstanding.some((line) => Number(receipt[line.id]) > 0)}
                  onClick={() => receive.mutate({ id: order.id, lines: outstanding.map((line) => ({ id: line.id, quantity: Number(receipt[line.id]) || 0 })).filter((line) => line.quantity > 0) })}
                  className="btn btn-primary gap-1.5"
                >
                  <PackageCheck className="h-4 w-4" /> {receive.isPending ? "Receiving…" : "Receive into stock"}
                </button>
              </div>
              {receive.isError && errorLine(receive.error, "The goods could not be received.")}
            </ActionPanel>
          )}

          {isOpenOrder(order) && !order.items.some((line) => line.quantity_received > 0) && (
            <ReasonPanel
              title="Cancel the order"
              description={order.status === "ordered" ? "Tell the supplier too; nothing has been received yet." : undefined}
              placeholder="e.g. The supplier cannot deliver before the term starts."
              context={{ key: "inventory.note", params: { kind: "order_cancel", id: order.id } }}
              button="Cancel the order"
              danger
              busy={cancel.isPending}
              error={cancel.error}
              errorText="The order could not be cancelled."
              onSubmit={(reason) => cancel.mutate({ id: order.id, reason })}
            />
          )}
        </DetailsModal>
      )}

      {(creating || draft) && (
        <OrderForm
          suppliers={suppliers}
          items={items}
          initialLines={draft ?? undefined}
          onClose={() => {
            setCreating(false);
            onDraftDone();
          }}
          onDone={(saved) => {
            setCreating(false);
            onDraftDone();
            done(`${saved.order_number} saved as a draft for ${money(saved.total_amount)}. Submit it for approval when it is ready.`);
            setViewing(saved.id);
          }}
        />
      )}
    </div>
  );
}
